/**
 * Component Name: Icon
 *
 * Description:
 * The Icon component is a lightweight utility designed to render Material icons
 * in a consistent style across the application. It supports flexible integration
 * into various UI elements, including buttons, headings, and callouts.
 *
 * Usage:
 * Use the Icon component to display Material icons in your interface.
 * Pass the icon name as a prop to dynamically render the desired icon.
 *
 * Example:
 * ```tsx
 * <Icon icon="info" />
 * ```
 *
 * Props:
 * - `icon` (string): The name of the Material icon to display.
 *   Ensure the name matches an available Material icon.
 *
 * Features:
 * - Dynamically renders any Material icon using the `material-symbols-outlined` class.
 * - Provides consistent styling for icons across the application.
 * - Easily extendable for additional customization via `Icon.styles.scss`.
 *
 * Notes:
 * - The component relies on the Material Icons font. Ensure the font is properly
 *   imported in your project for the icons to display correctly.
 * - Styling for the component is located in `Icon.styles.scss`. Customize as needed.
 * - Use the `icon` prop to specify the icon to render. This component does not
 *   handle fallback logic for invalid or missing icon names.
 *
 * Example of Rendered HTML:
 * ```html
 * <span class="icon material-symbols-outlined">info</span>
 * ```
 */

import React from 'react';
import './Icon.styles.scss';
import { IconProps } from './Icon.logic';

const Icon: React.FC<IconProps> = ({
  icon,
  iconImage,
  className = '',
  onClick,
}) => {
  return (
    <span onClick={onClick} className={`icon ${className}`}>
      {iconImage ? (
        <img src={iconImage} alt="icon" className={`icon-image`} />
      ) : (
        <span className="icon-mui material-symbols-outlined">{icon}</span>
      )}
    </span>
  );
};

export default Icon;
